import { useState, useEffect } from 'react';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Checkbox,
    Spinner
} from '@chakra-ui/react';
import { twMerge } from 'tailwind-merge';

import { UserCompany } from 'models/user/UserCompany';
import { Button, Label, TextArea } from 'common/materials';
import { useF1UserStatusRemark } from '../queries';

type Props = {
    isOpen: boolean;
    user?: UserCompany;
    onClose: () => void;
    onConfirm: (remark: string, activeWorkflow?: boolean, activeDms?: boolean) => void | Promise<void>;
};

function ActiveUserConfirmModal({ isOpen, user, onClose, onConfirm }: Props) {
    const useWorkflow = user?.workflow_status === 'true';
    const useDms = user?.dms_status === 'true';

    const [remark, setRemark] = useState('');
    const [activeWorkflow, setActiveWorkflow] = useState(false);
    const [activeDms, setActiveDms] = useState(false);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);

    const { data: f1UserStatus, isFetching: isFetchingF1UserStatus } = useF1UserStatusRemark({
        enabled: isOpen && !!user?.citizen_id,
        sid: user?.sid,
        platformId: user?.platform_id,
        username: user?.citizen_id
    });

    useEffect(() => {
        if (isOpen) {
            setActiveWorkflow(useWorkflow);
            setActiveDms(useDms);
            setInvalidFields([]);
        } else {
            setRemark('');
            setInvalidFields([]);
        }
    }, [isOpen, useWorkflow, useDms]);

    useEffect(() => {
        if (isOpen && f1UserStatus) {
            setRemark(f1UserStatus.UserStatusRemark ?? '');
        }
    }, [isOpen, f1UserStatus]);

    const handleConfirm = () => {
        if (!remark.trim()) {
            setInvalidFields(['remark']);
            return;
        }

        onConfirm(remark.trim(), activeWorkflow, activeDms);
    };

    return (
        <Modal size="xl" isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>ยืนยันการ Active ผู้ใช้งาน</ModalHeader>
                <ModalCloseButton />
                <ModalBody className="flex flex-col gap-4">
                    <p className="text-sm">
                        User <strong>{user?.citizen_id}</strong> จะถูกเปลี่ยนสถานะเป็น Active
                    </p>

                    <div className="space-y-1.5">
                        <div className="flex items-center">
                            <Label>หมายเหตุ</Label>
                            <span className="ml-0.5 text-sm text-red-600">*</span>
                            {isFetchingF1UserStatus && (
                                <Spinner
                                    thickness="2px"
                                    speed="0.65s"
                                    color="#e55c00"
                                    size="sm"
                                    className="ml-2"
                                />
                            )}
                        </div>
                        <TextArea
                            value={remark}
                            onChange={e => {
                                setRemark(e.target.value);
                                setInvalidFields(fields => fields.filter(f => f !== 'remark'));
                            }}
                            className={twMerge(invalidFields.includes('remark') ? 'border-red-600' : '')}
                        />
                    </div>

                    <div className="rounded border p-3 space-y-2">
                        <p className="text-sm font-medium">Workflow</p>
                        <Checkbox
                            colorScheme="primary"
                            isChecked={activeWorkflow}
                            onChange={e => setActiveWorkflow(e.target.checked)}
                        >
                            <span className="text-sm">
                                Active user <strong>{user?.citizen_id}</strong> ที่ระบบ Workflow
                            </span>
                        </Checkbox>
                    </div>

                    <div className="rounded border p-3 space-y-2">
                        <p className="text-sm font-medium">DMS</p>
                        <Checkbox
                            colorScheme="primary"
                            isChecked={activeDms}
                            onChange={e => setActiveDms(e.target.checked)}
                        >
                            <span className="text-sm">
                                Active user <strong>{user?.citizen_id}</strong> ที่ระบบ DMS
                            </span>
                        </Checkbox>
                    </div>
                </ModalBody>
                <ModalFooter className="gap-4">
                    <Button outline size="sm" className="text-gray-400" onClick={onClose}>
                        ยกเลิก
                    </Button>
                    <Button size="sm" onClick={handleConfirm} className="bg-primary-900 text-white">
                        ยืนยัน
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ActiveUserConfirmModal;
